/* eslint-disable object-curly-newline */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-len */
// DEPENDENCIES
import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
// COMPONENTS
import ReCAPTCHA from 'react-google-recaptcha';
// CUSTOM COMPONENTS
import CustomBlock from '../../../components/CustomBlock';
import Section from '../../../components/Section';
import ContentBlock from '../../../components/ContentBlock';
import FormBlock from '../../../components/FormBlock';
import InputBlock from '../../../components/InputBlock';
import Button from '../../../components/Button';
import TextAreaBlock from '../../../components/TextAreaBlock';
// SERVICES AND HELPERS
import * as auth from '../../../redux/authRedux';
import * as alertToast from '../../../redux/alertToastRedux';
import * as emailService from '../../../services/management/emailService';
import * as helper from '../../../helpers/helper';

const requestTypeOptions = [
  'Public Records Request',
  'General Information Request',
  'Service Request / Concern',
  'Other',
];

const responseMethodOptions = [
  'Pick Up in Person',
  'Mail',
  'Email',
];

const dateInputOptions = {
  altInput: true,
  altFormat: 'F j, Y',
};

const styles = {
  sectionHeader: {
    marginTop: '1.5rem',
    marginBottom: '1.25rem',
  },
  sectionDescription: {
    marginTop: '-0.75rem',
    marginBottom: '0.75rem',
    color: '#666',
    fontSize: '0.95rem',
  },
  optionGroup: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '0.75rem',
  },
  option: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.65rem',
    padding: '1.15rem 1rem',
    border: 'none',
    borderRadius: '300px',
    background: '#f7f7f7',
    color: '#888',
    fontWeight: 500,
    cursor: 'pointer',
    transition: '0.2s ease',
    outline: 'none',
    boxShadow: 'none',
    userSelect: 'none',
    lineHeight: 1,
  },
  optionSelected: {
    background: '#319749',
    color: '#fff',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
  },
  optionInput: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
  },
  error: {
    marginTop: '0.4rem',
    marginBottom: 0,
    color: '#dc3545',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  disabledInput: {
    background: 'repeating-linear-gradient(45deg, #ffffff, #ffffff 10px, #f5f5f5 10px, #f5f5f5 20px)',
    color: '#777',
    cursor: 'not-allowed',
    opacity: 1,
  },
};

const PublicRecordRequestTab = (props) => {
  const { showAlert } = props;
  const captchaRef = useRef();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRequestDate, setSelectedRequestDate] = useState(new Date().toISOString());

  const requestDate = selectedRequestDate ? moment(selectedRequestDate).format('MMMM D, YYYY') : '';
  const signatureDate = moment().format('MMMM D, YYYY');

  const getOptionId = (prefix, value) => `${prefix}-${value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      address: '',
      cityStateZip: '',
      phoneNumber: '',
      email: '',
      requestTypes: [],
      otherRequestType: '',
      message: '',
      preferredResponseMethod: '',
      signatureName: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First Name is required').max(100),
      lastName: Yup.string().required('Last Name is required').max(100),
      address: Yup.string().required('Address is required').max(150),
      cityStateZip: Yup.string().required('City / State / Zip is required').max(150),
      phoneNumber: Yup.string().max(50),
      email: Yup.string().required('Email Address is required').email().max(100),
      requestTypes: Yup.array().min(1, 'Please select at least one request type'),
      otherRequestType: Yup.string().when('requestTypes', {
        is: (requestTypes) => requestTypes && requestTypes.includes('Other'),
        then: (schema) => schema.required('Other Request Type is required').max(150),
        otherwise: (schema) => schema.max(150),
      }),
      message: Yup.string().required('Request Description is required'),
      preferredResponseMethod: Yup.string().required('Preferred Method of Response is required'),
      signatureName: Yup.string().required('Requester Signature / Typed Name is required').max(150),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!captchaToken) {
        setSubmitting(false);
        return;
      }

      if (!selectedRequestDate) {
        showAlert({ text: 'Date of Request is required.', state: 'error' });
        setSubmitting(false);
        return;
      }

      setIsLoading(true);

      const submissionData = {
        firstName: values.firstName,
        lastName: values.lastName,
        address: values.address,
        cityStateZip: values.cityStateZip,
        phoneNumber: values.phoneNumber,
        email: values.email,
        requestDate,
        requestType: values.requestTypes.join(', '),
        otherRequestType: values.otherRequestType,
        message: values.message,
        preferredResponseMethod: values.preferredResponseMethod,
        signatureName: values.signatureName,
        reCaptchaToken: captchaToken,
      };

      await emailService.sendPublicRecordRequestEmail(submissionData).then(() => {
        showAlert({ text: 'Success! Your public records request has been received.', state: 'success' });
        resetForm();
        setSelectedRequestDate(new Date().toISOString());
      }).catch((ex) => {
        showAlert({ text: ex.message, state: 'error' });
      }).finally(() => {
        if (captchaRef.current) {
          captchaRef.current.reset();
        }
        setCaptchaToken(null);
        setSubmitting(false);
        setIsLoading(false);
      });
    },
  });

  const showFieldError = (fieldName) => (
    (formik.touched[fieldName] || formik.submitCount > 0) ? formik.errors[fieldName] : ''
  );

  const handleRequestTypeChange = (option) => {
    const isSelected = formik.values.requestTypes.includes(option);
    const requestTypes = isSelected
      ? formik.values.requestTypes.filter((requestType) => requestType !== option)
      : [...formik.values.requestTypes, option];

    formik.setFieldValue('requestTypes', requestTypes);

    if (option === 'Other' && isSelected) {
      formik.setFieldValue('otherRequestType', '');
    }
  };

  const handleResponseMethodChange = (option) => {
    formik.setFieldValue('preferredResponseMethod', option);
  };

  return (
    <>
      <CustomBlock className="mb-40">
        <h2 className="size-xl fw-700 secondary--clr mb-15">Public Records Request</h2>
        <p className="mid-grey--clr size-mdlg">
          Use the form below to submit a public records request to <span className="fw-500">Livingston Parish Gravity Drainage District № 2</span>.
        </p>
      </CustomBlock>

      <FormBlock className="extended public-record-request-form">
        <Section hasNoContainer>
          <ContentBlock className="static-label dark--clr" cols={12}>
            <h3 className="size-mdlg fw-500 primary--clr mb-20" style={styles.sectionHeader}>Request Details</h3>
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={6}>
            <InputBlock
              isRequired
              type="date"
              label="Date of Request"
              placeholder="Set Date"
              value={selectedRequestDate}
              options={dateInputOptions}
              onChange={(e) => setSelectedRequestDate(e.length > 0 ? moment(e[0]).toISOString() : '')}
            />
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={12}>
            <h3 className="size-mdlg fw-500 primary--clr mt-40 mb-20" style={styles.sectionHeader}>Requester Information</h3>
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={6}>
            <InputBlock
              isRequired
              label="First Name"
              placeholder="John"
              errorMessage={showFieldError('firstName')}
              inputState={`${helper.getInputClasses(formik, 'firstName')}`}
              {...formik.getFieldProps('firstName')}
            />
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={6}>
            <InputBlock
              isRequired
              label="Last Name"
              placeholder="Smith"
              errorMessage={showFieldError('lastName')}
              inputState={`${helper.getInputClasses(formik, 'lastName')}`}
              {...formik.getFieldProps('lastName')}
            />
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={6}>
            <InputBlock
              isRequired
              label="Address"
              placeholder="Street address"
              errorMessage={showFieldError('address')}
              inputState={`${helper.getInputClasses(formik, 'address')}`}
              {...formik.getFieldProps('address')}
            />
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={6}>
            <InputBlock
              isRequired
              label="City / State / Zip"
              placeholder="Denham Springs, LA 70706"
              errorMessage={showFieldError('cityStateZip')}
              inputState={`${helper.getInputClasses(formik, 'cityStateZip')}`}
              {...formik.getFieldProps('cityStateZip')}
            />
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={6}>
            <InputBlock
              label="Phone Number"
              placeholder="(555) 555-5555"
              errorMessage={showFieldError('phoneNumber')}
              inputState={`${helper.getInputClasses(formik, 'phoneNumber')}`}
              mask="(999) 999-9999"
              {...formik.getFieldProps('phoneNumber')}
            />
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={6}>
            <InputBlock
              isRequired
              label="Email Address"
              placeholder="johnsmith@example.com"
              errorMessage={showFieldError('email')}
              inputState={`${helper.getInputClasses(formik, 'email')}`}
              {...formik.getFieldProps('email')}
            />
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={12}>
            <h3 className="size-mdlg fw-500 primary--clr mt-40 mb-20" style={styles.sectionHeader}>Type of Request</h3>
            <p className="size-xs mb-10 text-muted" style={styles.sectionDescription}>
              Public records requests are handled in accordance with Louisiana Public Records Law. Select all that apply.
            </p>
          </ContentBlock>

          <ContentBlock className="static-label dark--clr mt-10" cols={12}>
            <CustomBlock style={styles.optionGroup}>
              {requestTypeOptions.map((option) => {
                const optionId = getOptionId('request-type', option);
                const isSelected = formik.values.requestTypes.includes(option);

                return (
                  <label
                    htmlFor={optionId}
                    className="size-xs"
                    style={{
                      ...styles.option,
                      ...(isSelected ? styles.optionSelected : {}),
                    }}
                    key={option}
                  >
                    <input
                      id={optionId}
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleRequestTypeChange(option)}
                      style={styles.optionInput}
                    />
                    {option}
                  </label>
                );
              })}
            </CustomBlock>

            {showFieldError('requestTypes') && (
              <p style={styles.error}>{showFieldError('requestTypes')}</p>
            )}
          </ContentBlock>

          {formik.values.requestTypes.includes('Other') && (
            <ContentBlock className="static-label dark--clr mt-20" cols={6}>
              <InputBlock
                isRequired
                label="Other Request Type"
                placeholder="Please specify"
                errorMessage={showFieldError('otherRequestType')}
                inputState={`${helper.getInputClasses(formik, 'otherRequestType')}`}
                {...formik.getFieldProps('otherRequestType')}
              />
            </ContentBlock>
          )}

          <ContentBlock className="static-label dark--clr" cols={12}>
            <h3 className="size-mdlg fw-500 primary--clr mt-60 mb-20" style={styles.sectionHeader}>Description of Request</h3>
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={12}>
            <TextAreaBlock
              isRequired
              label="Request Description"
              placeholder="Please describe the records, information, service request, or concern..."
              errorMessage={showFieldError('message')}
              inputState={`${helper.getInputClasses(formik, 'message')}`}
              {...formik.getFieldProps('message')}
            />
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={12}>
            <h3 className="size-mdlg fw-500 primary--clr mt-40 mb-20" style={styles.sectionHeader}>Preferred Method of Response</h3>
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={12}>
            <CustomBlock style={styles.optionGroup}>
              {responseMethodOptions.map((option) => {
                const optionId = getOptionId('response-method', option);
                const isSelected = formik.values.preferredResponseMethod === option;

                return (
                  <label
                    htmlFor={optionId}
                    className="size-xs"
                    style={{
                      ...styles.option,
                      ...(isSelected ? styles.optionSelected : {}),
                    }}
                    key={option}
                  >
                    <input
                      id={optionId}
                      type="radio"
                      name="preferredResponseMethod"
                      checked={isSelected}
                      onChange={() => handleResponseMethodChange(option)}
                      style={styles.optionInput}
                    />
                    {option}
                  </label>
                );
              })}
            </CustomBlock>

            {showFieldError('preferredResponseMethod') && (
              <p style={styles.error}>{showFieldError('preferredResponseMethod')}</p>
            )}
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={12}>
            <h3 className="size-mdlg fw-500 primary--clr mt-60 mb-20" style={styles.sectionHeader}>Signature</h3>
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={6}>
            <InputBlock
              isRequired
              label="Requester Signature / Typed Name"
              placeholder="Type your full name"
              errorMessage={showFieldError('signatureName')}
              inputState={`${helper.getInputClasses(formik, 'signatureName')}`}
              {...formik.getFieldProps('signatureName')}
            />
          </ContentBlock>

          <ContentBlock className="static-label dark--clr" cols={6}>
            <InputBlock
              label="Signature Date"
              value={signatureDate}
              disabled
              readOnly
              inputState="disabled"
              style={styles.disabledInput}
            />
          </ContentBlock>
        </Section>

        <CustomBlock className="content-container--actions mt-40">
          {captchaToken == null ? (
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={setCaptchaToken}
              className="recaptcha"
            />
          ) : (
            <Button
              isLoading={isLoading}
              isDisabled={isLoading}
              text="Submit Request"
              className="secondary--sbg"
              size="sm"
              onClick={formik.handleSubmit}
            />
          )}
        </CustomBlock>
      </FormBlock>
    </>
  );
};

const mapStateFromProps = (state) => ({ auth: state.auth });

export default connect(mapStateFromProps, {
  ...auth.actions,
  ...alertToast.actions
})(PublicRecordRequestTab);